import React, { useState } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Check from "@mui/icons-material/Check";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import VideoLabelIcon from "@mui/icons-material/VideoLabel";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import CompanyDetails from "./Steps/CompanyDetails";
import Goals from "./Steps/Goals";
import ReviewReport from "./Steps/ReviewReport";
import ReportStatus from "./Steps/ReportStatus";
import { useFormik } from "formik";
import initialState from "./InitialState";
import validationSchema from "./validation";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyDetailsThunkAction } from "redux/growPlanAssessment/actions";
import { selectGrowthPlan } from "redux/growPlanAssessment/selectors";
import moment from "moment";
import * as Yup from "yup";
import { submitGoalsDetailsThunkAction } from "redux/growPlanAssessment/actions";
import { confirmGoalsDetailsThunkAction } from "redux/growPlanAssessment/actions";
import BackdropLoader from "components/BackdropLoader/BackdropLoader";
import { useBeforeUnload } from "hooks/useBeforeUnload";
import { useBlockNavigation } from "hooks/useBlockNavigation";
import { usePrompt } from "hooks/usePrompt";
import { resetGrowthPlanAssessment } from "redux/growPlanAssessment/actions";
import { fetchGoalsListThunkAction } from "redux/growPlanAssessment/actions";
import { fetchDownloadGrowthPlanReportThunkAction } from "redux/growPlanAssessment/actions";
import toast from "react-hot-toast";

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)"
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#1273EB"
    }
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#1273EB"
    }
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "#eaeaf0",
    borderTopWidth: 3,
    borderRadius: 1
  }
}));

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: "#1273EB"
  }),
  "& .QontoStepIcon-completedIcon": {
    color: "#1273EB",
    zIndex: 1,
    fontSize: 18
  },
  "& .QontoStepIcon-circle": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor"
  }
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

QontoStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool
};

const steps = ["Company Details", "Goals", "Review Report", "Report Status"];

function GrowthPlanAssessmentForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [planId, setPlanId] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);

  const dispatch = useDispatch();
  const {
    isCompanyDetailsLoading,
    companyDetails,
    companyDetailsError,
    isGoalsSubmitting,
    goalsSubmissionError,
    isGoalConfirming,
    goalConfirmationError,
    loadingMessage
  } = useSelector(selectGrowthPlan);

  const [isDirty, setIsDirty] = useState(false);

  useBeforeUnload(isDirty); // for refresh
  useBlockNavigation(isDirty, "You have unsaved changes. Are you sure you want to leave?"); // for route change
  usePrompt(isDirty, "You have unsaved changes. Are you sure you want to leave?");

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: Yup.object().shape({
      mission: Yup.string()
        .trim()
        .required("Mission is required")
        .max(500, "Mission must be at most 500 characters long"),
      vision: Yup.string()
        .trim()
        .required("Vision is required")
        .max(500, "Vision must be at most 500 characters long"),
      companyOverview: Yup.string()
        .trim()
        .required("Company Overview is required")
        .max(1000, "Company Overview must be at most 1000 characters long"),
      companyEstablishmentDate: Yup.date().required("Company Establishment Date is required"),
      goals: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string(),
            description: Yup.string().trim().required("Goal description is required"),
            priority: Yup.string()
              .required("Priority is required")
              .oneOf(["High", "Mid", "Low"], "Priority must be High, Medium, or Low"),
            outComes: Yup.array()
              .of(
                Yup.object().shape({
                  currentStatus: Yup.string().trim().required("Current Status is required"),
                  desiredStatus: Yup.string().trim().required("Desired Status is required")
                })
              )
              .min(1, "At least one outcome is required")
              .required("Outcomes are required"),
            duration: Yup.string().when([], (schema) => {
              if (activeStep === 2) {
                return schema.required("Duration is required");
              }
              return schema;
            }),
            personName: Yup.string()
              .trim()
              .when([], (schema) => {
                if (activeStep === 2) {
                  return schema.required("Person Name is required");
                }
                return schema;
              }),
            actions: Yup.array().of(Yup.string().required("Action is required"))
          })
        )
        .min(1, "At least one goal is required")
        .required("Goals are required")
    }),
    onSubmit: async (values) => {}
  });

  React.useEffect(() => {
    let timerId;
    setGeneralLoading(true);
    timerId = setTimeout(() => {
      setGeneralLoading(false);
    }, 2000);

    dispatch(
      fetchCompanyDetailsThunkAction(
        () => {},
        () => {}
      )
    );

    dispatch(fetchGoalsListThunkAction());

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, []);

  React.useEffect(() => {
    if (companyDetails) {
      formik.setValues({
        startDate: new Date(),
        industryName: companyDetails?.industries || "",
        companyName: companyDetails?.company || "",
        mission: companyDetails?.mission || "",
        vision: companyDetails?.vision || "",
        companyOverview: companyDetails?.company_overview || "",
        companyEstablishmentDate: companyDetails?.company_establishment_date || "",
        goals:
          companyDetails?.goals?.length > 0
            ? companyDetails.goals.map((goal) => ({
                ...goal,
                personName: goal.accountable_person_name || "",
                duration: goal.complete_duration || "",
                outComes: goal.outcomes?.map((outcome) => ({
                  id: outcome.id || "",
                  currentStatus: outcome.current_status || "",
                  desiredStatus: outcome.desired_outcome || "",
                  actionPlans: outcome.action_plans || ""
                }))
              }))
            : []
      });
    }
  }, [companyDetails]);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  function submitGoalsHandler() {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));

    const reqBody = {
      industry: formik.values.industryName,
      company_name: formik.values.companyName,
      company_overview: formik.values.companyOverview,
      mission: formik.values.mission,
      vision: formik.values.vision,
      // 2025-06-01 12:00:00
      company_establishment_date: moment(new Date(formik.values.companyEstablishmentDate)).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
      goals: formik.values.goals.map((goal) => {
        return {
          title: goal.name,
          description: goal.description,
          priority: goal.priority,
          goal_outcomes: goal.outComes.map((outcome) => {
            return {
              current_status: outcome.currentStatus,
              desired_outcome: outcome.desiredStatus
            };
          })
        };
      })
    };
    formik.setFieldValue("goals", []);

    setGeneralLoading(true);
    setTimeout(() => {
      setGeneralLoading(false);
    }, 2000);

    dispatch(
      submitGoalsDetailsThunkAction(
        reqBody,
        () => {},
        () => {}
      )
    );
  }

  function confirmGoalsHandler() {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    setIsDirty(false);

    const reqBody = formik.values.goals.map((goal) => {
      return {
        goal_id: goal.id,
        accountable_person_name: goal.personName,
        complete_duration: goal.duration
      };
    });
    setPlanId(companyDetails?.id);

    setGeneralLoading(true);
    setTimeout(() => {
      setGeneralLoading(false);
    }, 2000);
    dispatch(
      confirmGoalsDetailsThunkAction(companyDetails?.id, reqBody, () => {
        setTimeout(() => {
          dispatch(resetGrowthPlanAssessment());
        }, 2000);
      })
    );
  }

  const downloadReportHandler = React.useCallback(() => {
    setIsDownloading(true);
    toast.loading("Fetching Growth Plan Report...");
    dispatch(
      fetchDownloadGrowthPlanReportThunkAction(
        planId,
        () => {
          toast.dismiss();
          setIsDownloading(false);
        },
        () => {
          toast.dismiss();
          setIsDownloading(false);
          toast.error("Failed to fetch growth plan report.");
        }
      )
    );
  }, [planId, dispatch, setIsDownloading]);

  // console.log(formik.values, "Formik Values");
  // console.log(formik.errors, "Formik Errors");
  // console.log(formik.touched, "Formik Touched");
  // console.log(
  //   isCompanyDetailsLoading,
  //   companyDetails,
  //   companyDetailsError,
  //   "Growth Plan Assessment State"
  // );

  // console.log(companyDetails, formik.values, "Company Details and Formik Values");

  return (
    <>
      {generalLoading ||
      isCompanyDetailsLoading ||
      isGoalsSubmitting ||
      isGoalConfirming ||
      isDownloading ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999
          }}
        >
          <BackdropLoader message={loadingMessage} />
        </div>
      ) : null}
      <div className="px-3 w-100">
        <div className="d-flex justify-content-center my-2 w-100">
          <h4>Growth Plan Assessment</h4>
        </div>

        <div className="container">
          <Box>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              className="bg-light p-3 w-auto rounded"
              connector={<QontoConnector />}
            >
              {steps.map((label, idx) => (
                <Step key={label} className="">
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {companyDetailsError || goalsSubmissionError || goalConfirmationError ? (
            <div
              className="w-100 d-flex justify-content-center align-items-center text-danger"
              style={{
                minHeight: "50vh"
              }}
            >
              {companyDetailsError || goalsSubmissionError || goalConfirmationError}
            </div>
          ) : (
            <form onChange={() => setIsDirty(true)} onSubmit={formik.handleSubmit}>
              <div className="mt-4">
                {activeStep === 0 && <CompanyDetails formik={formik} handleNext={handleNext} />}
                {activeStep === 1 && (
                  <Goals formik={formik} handleNext={submitGoalsHandler} handleBack={handleBack} />
                )}
                {activeStep === 2 && (
                  <ReviewReport
                    formik={formik}
                    handleNext={confirmGoalsHandler}
                    handleBack={handleBack}
                  />
                )}
                {activeStep === 3 && !isGoalConfirming && (
                  <ReportStatus downloadReportHandler={downloadReportHandler} />
                )}
              </div>
              {/* <div className="d-flex justify-content-between mt-4">
            {activeStep === steps.length - 1 ? null : (
              <>
                {activeStep > 0 ? (
                  <button onClick={handleBack} className="btn btn-secondary">
                    Back
                  </button>
                ) : (
                  <div></div>
                )}

                <button
                  onClick={handleNext}
                  disabled={activeStep === steps.length - 1}
                  className="btn btn-primary"
                >
                  Next
                </button>
              </>
            )}
          </div> */}
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export default GrowthPlanAssessmentForm;
